<template>
  <section class="mb-6">
    <h2 class="text-h6 mb-1">{{ title }}</h2>
    <p v-if="schedule" class="help-text mb-3">{{ schedule }}</p>
    <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>
    <v-row>
      <v-col cols="12" :md="hasRdp ? 4 : 12">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title"><v-icon left>mdi-server</v-icon>EC2</v-card-title>
          <v-card-text>
            <div class="state-row">
              <v-chip :color="stateColor" dark>{{ status.state || "desconhecido" }}</v-chip>
              <span class="caption grey--text text--darken-1">{{ status.instanceType || "-" }}</span>
            </div>
            <div class="detail-list mt-4">
              <div><span>Instância</span><strong>{{ status.instanceId || "-" }}</strong></div>
              <div><span>Região</span><strong>{{ status.region || "-" }}</strong></div>
              <div><span>Status EC2</span><strong>{{ status.instanceStatus || "-" }}</strong></div>
              <div><span>Status sistema</span><strong>{{ status.systemStatus || "-" }}</strong></div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col v-if="hasRdp" cols="12" md="8">
        <v-card outlined class="fill-height">
          <v-card-title class="panel-title"><v-icon left>mdi-remote-desktop</v-icon>Conexão RDP</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6"><div class="detail-list">
                <div><span>Host atual</span><strong>{{ status.rdpHost || "-" }}</strong></div>
                <div><span>IP público</span><strong>{{ status.publicIpAddress || "-" }}</strong></div>
                <div><span>DNS público</span><strong>{{ status.publicDnsName || "-" }}</strong></div>
              </div></v-col>
              <v-col cols="12" md="6"><div class="detail-list">
                <div><span>Porta</span><strong>{{ status.rdpPort || 3389 }}</strong></div>
                <div><span>Usuário</span><strong>{{ status.rdpUsername || "Administrator" }}</strong></div>
                <div v-if="agentLabel"><span>Agente EC2</span><strong>{{ agentLabel }}</strong></div>
              </div></v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-card outlined class="mt-4">
      <v-card-title class="panel-title"><v-icon left>mdi-console</v-icon>Máquina</v-card-title>
      <v-card-text>
        <div class="action-grid">
          <v-btn color="success" :loading="busy.start" :disabled="runningLike || !status.instanceId" @click="changePower('start')"><v-icon left>mdi-play</v-icon>Ligar EC2</v-btn>
          <v-btn color="error" outlined :loading="busy.stop" :disabled="stoppedLike || !status.instanceId" @click="changePower('stop')"><v-icon left>mdi-stop</v-icon>Desligar EC2</v-btn>
          <v-btn v-if="hasRdp" color="primary" :loading="busy.rdp" :disabled="!status.rdpAvailable" @click="downloadRdp"><v-icon left>mdi-download</v-icon>Baixar RDP</v-btn>
          <v-btn outlined :disabled="!status.rdpHost" @click="copyHost"><v-icon left>mdi-content-copy</v-icon>Copiar host</v-btn>
        </div>
        <v-progress-linear v-if="transitioning" indeterminate color="primary" class="mt-4" />
        <p v-if="hasRdp" class="help-text mt-3 mb-0">O RDP traz o host e o usuário. Neste dispositivo, a senha já salva no cliente de Área de Trabalho Remota pode ser reutilizada.</p>
      </v-card-text>
    </v-card>
  </section>
</template>

<script>
import { ec2CodexAPI } from "../API/Ec2CodexAPI";

export default {
  name: "Ec2MachineCard",
  props: { machineKey: { type: String, required: true }, title: { type: String, required: true }, schedule: { type: String, default: "" }, agentLabel: { type: String, default: "" }, hasRdp: { type: Boolean, default: true } },
  data: () => ({ status: {}, error: "", busy: { start: false, stop: false, rdp: false } }),
  computed: {
    state() { return String(this.status.state || "").toLowerCase(); },
    runningLike() { return ["pending", "running"].includes(this.state); },
    stoppedLike() { return ["stopping", "stopped", "shutting-down", "terminated"].includes(this.state); },
    transitioning() { return ["pending", "stopping", "shutting-down"].includes(this.state); },
    stateColor() { return ({ running: "success", pending: "warning", stopping: "warning", stopped: "grey", terminated: "error" })[this.state] || "primary"; },
  },
  mounted() { this.refresh(); },
  methods: {
    async refresh() {
      try { this.status = (await ec2CodexAPI.machineStatus(this.machineKey)).data || {}; this.error = ""; }
      catch (error) { this.error = this.extractError(error, "Não foi possível consultar a EC2."); }
    },
    async changePower(action) {
      this.busy[action] = true; this.error = "";
      try { this.status = (await ec2CodexAPI.machinePower(this.machineKey, action)).data || {}; }
      catch (error) { this.error = this.extractError(error, "Não foi possível alterar o estado da EC2."); }
      finally { this.busy[action] = false; }
    },
    async downloadRdp() {
      this.busy.rdp = true; this.error = "";
      try {
        const response = await ec2CodexAPI.machineRdp(this.machineKey);
        const url = URL.createObjectURL(new Blob([response.data], { type: "application/x-rdp" }));
        const link = document.createElement("a"); link.href = url; link.download = this.machineKey === "backup" ? "ec2-backups.rdp" : "codex-ec2-ohio.rdp";
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (error) { this.error = this.extractError(error, "Não foi possível baixar o RDP."); }
      finally { this.busy.rdp = false; }
    },
    async copyHost() {
      try { await navigator.clipboard.writeText(this.status.rdpHost); }
      catch (error) { this.error = "Não foi possível copiar o host."; }
    },
    extractError(error, fallback) {
      const data = error && error.response && error.response.data;
      if (data && data.mensagem) return data.mensagem;
      if (error && error.response) return `${fallback} (HTTP ${error.response.status}).`;
      return (error && error.message) || fallback;
    },
  },
};
</script>

<style scoped>
.panel-title { font-size: 16px; font-weight: 600; }
.state-row { align-items: center; display: flex; gap: 12px; }
.detail-list { display: grid; gap: 12px; }
.detail-list div { display: grid; gap: 2px; min-width: 0; }
.detail-list span { color: #6b7280; font-size: 12px; text-transform: uppercase; }
.detail-list strong { color: #1f2937; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.help-text { color: #4b5563; font-size: 13px; }
.action-grid { display: flex; flex-wrap: wrap; gap: 10px; }
@media (max-width: 600px) { .action-grid .v-btn { width: 100%; } }
</style>
